import { useState } from "react";
import { getMetaData, getCurrentUserId } from "../../service/zendesk";
import { updateManyTickets, formatPayload, getJobResult, getUser } from "../../service/ticket";
import { getZendeskUrl } from "../../service/zendesk";
import { TicketPayload } from "../../@types/ticket";
import { useNotification } from "../../components/notification";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useHistory } from "../../hook/useHistory";
import { useQueryClient } from "@tanstack/react-query";

type TicketData = {
    field: string;
    value: any;
};

interface UseTicketUpdateProps {
    results: Record<string, string>[];
    ticketData: TicketData[];
    subject: string;
    comment: string;
    typeComment: string;
    status: string;
    type: string;
    applyFilter: (updated?: boolean) => void;
    resetForm: () => void;
}

export function useTicketUpdate({ results, ticketData, subject, comment, typeComment, applyFilter, resetForm }: UseTicketUpdateProps) {
    const { showNotification } = useNotification();
    const [updating, setUpdating] = useState(false);
    const [updateProgress, setUpdateProgress] = useState(0);
    const [failedTickets, setFailedTickets] = useState<any[]>([]);
    const { createBulkHistory } = useHistory();
    const queryClient = useQueryClient();

    function extractJobId(jobUrl: string): string {
        return jobUrl.split("/").pop()?.replace(".json", "") ?? "";
    }

    function exportFailedTicketsToXLSX() {
        if (failedTickets.length === 0) return;

        const worksheet = XLSX.utils.json_to_sheet(
            failedTickets.map((r: any) => ({
                "Ticket ID": r.id,
                "Título": r.title,
                "Status": r.status,
                "Erro": r.errors || "Erro desconhecido"
            }))
        );

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Erros");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, `tickets_com_erro_${new Date().toISOString().split("T")[0]}.xlsx`);
    }

    async function handleUpdateTickets() {
        if (!results.length) return;

        setUpdating(true);
        setUpdateProgress(0);

        try {
            showNotification({
                type: "info",
                title: "Aguarde um momento",
                message: "A edição massiva pode levar algum tempo, dependendo da quantidade de dados.",
            });
            const metadata = await getMetaData();
            const currentUserId = await getCurrentUserId();
            const ticketIds = results.map(r => parseInt(r.id));

            const ticketContent: any = {};

            const statusFromTicketData = ticketData.find(
                item => item.field === "status"
            )?.value;


            if (statusFromTicketData && statusFromTicketData !== "-") {
                ticketContent.status = statusFromTicketData;
            }

            if (subject) {
                ticketContent.subject = subject;
            }
            if (comment && typeComment) {
                ticketContent.comment = {
                    body: comment,
                    public: typeComment === "public",
                    author_id: currentUserId
                };
            }

            const formatted = formatPayload(ticketData);

            if (formatted.requester_email) {
                try {
                    const userResult = await getUser([{ searchOption: "Email", value: formatted.requester_email, ID: "temp" }]);
                    if (userResult.users && userResult.users.length > 0) {
                        ticketContent.requester_id = userResult.users[0].id;
                    } else {
                        showNotification({
                            type: "warning",
                            title: "Usuário não encontrado",
                            message: `O email ${formatted.requester_email} não foi encontrado no Zendesk.`,
                        });
                    }
                } catch (error) {
                    console.error("Erro ao buscar usuário:", error);
                }
                delete formatted.requester_email;
            }

            Object.assign(ticketContent, formatted);

            const payload: TicketPayload = {
                email: metadata.emailAdmin,
                token: metadata.apiToken,
                subdomain: getZendeskUrl(),
                tickets_to_update: ticketIds,
                ticket: ticketContent
            };

            const jobUrl = await updateManyTickets(payload);
            const jobResults = await getJobResult(jobUrl, setUpdateProgress, 0);

            const total = jobResults.length;
            const successCount = jobResults.filter(r => r.success).length;
            const failedCount = total - successCount;
            const jobId = extractJobId(jobUrl);


            await createBulkHistory({
                action: "update",
                action_type: "ticket",
                requester: currentUserId,
                total_count: total,
                success_count: successCount,
                failed_count: failedCount,
                status:
                    failedCount === 0
                        ? "completed"
                        : successCount === 0
                            ? "failed"
                            : "partial",
                error_summary:
                    failedCount > 0 ? "Alguns tickets falharam na atualização" : undefined,
                job_ids: jobId,

            });

            queryClient.invalidateQueries({
                queryKey: ["bulk-history"]
            });

            const failedTicketsData = jobResults?.filter((r: any) => !r.success) || [];
            setFailedTickets(failedTicketsData);

            if (failedTicketsData.length > 0) {
                showNotification({
                    type: "warning",
                    title: `Atualização concluída com ${failedTicketsData.length} erros`,
                    message: `${failedTicketsData.length} tickets não foram atualizados. Baixe o relatório de erros.`,
                });
            } else {
                showNotification({
                    type: "success",
                    title: "Tickets atualizados com sucesso!",
                    message: "A edição massiva foi concluída com sucesso.",
                });
            }

            resetForm();
            applyFilter(true);

        } catch (error) {
            console.error(error);
            showNotification({
                type: "error",
                title: "Erro na atualização dos tickets",
                message: "Ocorreu um erro ao atualizar os tickets. Por favor, revise os dados e tente novamente.",
            });
        } finally {
            setUpdating(false);
            setUpdateProgress(0);
        }
    }

    return {
        updating,
        updateProgress,
        handleUpdateTickets,
        failedTickets,
        exportFailedTicketsToXLSX
    }
}